import { SelectionChangedEvent } from "ag-grid-community";
import { CompanyEntity } from "domain/entity/Company/CompanyEntity";
import _ from "lodash";
import { MessageConstant } from "presentation/constant/MessageConstant";
import { TariffCodeConstant } from "presentation/constant/TariffCode/TariffCodeConstant";
import { INITIAL_TARIFF_CODE_BY_COMP_COL_DEF } from "presentation/constant/TariffCodeByCompany/TariffCodeByCompanyColumnDefinition";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useTariffCodeByCompanyVM } from "presentation/hook/TariffCodeByCompany/useTariffCodeByCompanyVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useTariffCodeByCompanyTracked } from "presentation/store/TariffCodeByCompany/TariffCodeByCompanyProvider";
import { ConfirmDeleteModal } from "presentation/view/components/ConfirmDeleteModal";
import NbisTable from "presentation/view/components/TableWrapper/NbisTable";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader } from "veronica-ui-component/dist/component/core";


const TariffCodeByCompanyTablePanel: React.FC = () => {
    const [tariffCodeByCompanyState] = useTariffCodeByCompanyTracked();
    const tariffCodeByCompanyVM = useTariffCodeByCompanyVM();
    const gridRef: any = useRef(null);
    const { isShowAddPanel, isShowDetail, companyEntityList, selectedRows, } = tariffCodeByCompanyState;
    const [anainfoState] = useANAInfoTracked();
    const { allowCreate, allowDelete } = anainfoState;
    const messageBarVM = useMessageBarVM();
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isShowDelete, setIsShowDelete] = useState<boolean>(false);
    const [deletedRow, setDeletedRow] = useState<CompanyEntity[]>();


    const handleSelectionChange = useCallback((e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();

        tariffCodeByCompanyVM.updateSelectedRows(selectedRows);
    }, [tariffCodeByCompanyVM])

    // const handleRowDoubleClick = useCallback((entity: CompanyEntity) => {
    //     tariffCodeByCompanyVM.onSearchCompanyTariffCode(entity);
    // }, [tariffCodeByCompanyVM])


    const handleAddClick = useCallback(() => {
        tariffCodeByCompanyVM.onAddClick();
    }, [tariffCodeByCompanyVM])

    const handleReloadClick = useCallback(() => {
        setLoading(true);
        tariffCodeByCompanyVM.onSearchCompany().then((data) => {
            setLoading(false)
        }).catch(error => {
            setLoading(false)
        });
    }, [tariffCodeByCompanyVM])

    const handleDeleteClick = useCallback(async () => {
        if (_.isEmpty(selectedRows)) {
            messageBarVM.showError("Please select a record");
            return;
        }
        setIsShowDelete(true);
        setDeletedRow(selectedRows);
    }, [messageBarVM, selectedRows]);

    const handleCancel = () => {
        setIsShowDelete(false);
    }

    const handleDelete = useCallback(async () => {
        setIsShowDelete(false);
        setLoading(true);
        if (!deletedRow) return;
        const res = await tariffCodeByCompanyVM.onDelete(deletedRow);
        setLoading(false);
        if (res && res.success) {
            messageBarVM.showSuccess("Delete successfully.");
            tariffCodeByCompanyVM.onSearchCompany();
        } else {
            messageBarVM.showError(res.data ?? "Failed to delete company.")

        }
    }, [deletedRow, messageBarVM, tariffCodeByCompanyVM])

    const tbBtns = useMemo(() => {
        let btns = [];
        btns.push({
            id: 'onRefreshButton',
            icon: 'Icon-reload',
            title: 'Refresh'
        })
        if (allowDelete && (!isShowAddPanel && !isShowDetail)) {
            btns.push({
                id: 'onDeleteButton',
                icon: 'Icon-trash',
                title: 'Delete'
            })
        }
        return btns;
    }, [allowDelete, isShowAddPanel, isShowDetail])


    const isRowSelectable = useCallback((params: any) => {
        return !isShowDetail;
    }, [isShowDetail]);

    const memoTariffCodeTable = useMemo(() => {
        return (
            <NbisTable
                id='tariff-code-by-company-table'
                headerLabel={TariffCodeConstant.CompanyTariffCode.TABLE_TITLE}
                headerActionButtons={tbBtns}
                isNewColumnSetting={true}
                columns={INITIAL_TARIFF_CODE_BY_COMP_COL_DEF}
                data={companyEntityList ?? []}
                showPaginator={false}
                editable={false}
                showAddIcon={allowCreate && (!isShowAddPanel && !isShowDetail)}
                onAddClick={handleAddClick}
                onDeleteButton={handleDeleteClick}
                showDeleteButton={false}
                showReloadIcon={false}
                onRefreshButton={handleReloadClick}
                isScrollHighlighted={true}
                // selectionMode={true}
                isRowHighligted={true}
                rowSelection={"multiple"}
                handleSelectionChanged={handleSelectionChange}
                // onRowDoubleClick={(e: any, entity: CompanyEntity) => handleRowDoubleClick(entity)}
                gridHeight="customHeight"
                customHeight="calc(100vh - 110px)"
                ref={gridRef}
                handleRowSelectable={isRowSelectable}
            />
        );
    }, [tbBtns, companyEntityList, allowCreate, isShowAddPanel, isShowDetail, handleAddClick, handleDeleteClick, handleReloadClick, handleSelectionChange, isRowSelectable])

    useEffect(() => {
        if (gridRef.current && gridRef.current.gridRef && gridRef.current.gridRef.current && gridRef.current.gridRef.current.api) {
            gridRef.current.gridRef.current.api.redrawRows();
        }
    }, [isShowDetail]);

    return <>
        {isLoading && <Loader Indicator="Spinner" size="Medium" />}
        <TableWrapper>
            {memoTariffCodeTable}
        </TableWrapper>

        <ConfirmDeleteModal headerText={`${WorkspaceConstant.Common.BUTTON_DELETE} ${TariffCodeConstant.CompanyTariffCode.TABLE_TITLE}`}
            contentText={`${MessageConstant.common.DELETE_ALERT_TITLE}\n${MessageConstant.common.DELETE_ALERT_MESSAGE}`}
            visible={isShowDelete}
            onCancelClick={handleCancel}
            onDeleteClick={handleDelete}
        />
    </>;
}

export default memo(TariffCodeByCompanyTablePanel);
